import { addDays, roundToNearestMinutes, startOfDay } from 'date-fns';
// Usage existed before banning this import. This should eventually be cleaned up to use utils
// from `core`.
// eslint-disable-next-line no-restricted-imports
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

const roundToHour = (date: Date) => {
  const p = 60 * 60 * 1000; // milliseconds in an hour
  return new Date(Math.round(date.getTime() / p) * p);
};

const getLabelIntervalMins = (startTime: number, endTime: number) => {
  const timeRangeHours = (endTime - startTime) / 60_000 / 60;

  if (timeRangeHours <= 0.5) return 1;
  if (timeRangeHours <= 1) return 5;
  if (timeRangeHours <= 3) return 20;
  return 60;
};

const roundedTimestamp = (timestamp: number, labelIntervalMins: number) => {
  if (labelIntervalMins === 60) {
    return roundToHour(new Date(timestamp)).getTime();
  }

  return roundToNearestMinutes(new Date(timestamp), {
    nearestTo: labelIntervalMins,
    roundingMethod: 'round',
  }).getTime();
};

const getDayStartMs = (date: Date, timeZone: string): number => {
  if (timeZone === 'local') {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }
  const zonedDate = utcToZonedTime(date, timeZone);
  const zonedStartOfDay = startOfDay(zonedDate);
  return zonedTimeToUtc(zonedStartOfDay, timeZone).getTime();
};

const getNextMidnight = (currentMidnight: number, timeZone: string): number => {
  const currentDate = new Date(currentMidnight);
  const nextDay = addDays(currentDate, 1);
  return getDayStartMs(nextDay, timeZone);
};

interface GetDatesParams {
  minTimestamp: number;
  maxTimestamp: number;
  width: number;
  timeZone: string;
  perLabelWidth: number;
}

export const PER_LABEL_WIDTH_24 = 50;
export const PER_LABEL_WIDTH_12 = 90;

const MS_PER_DAY = 24 * 60 * 60 * 1_000;
const MS_PER_MIN = 1_000 * 60;

/**
 * Generates timestamps for axis labels within the given time range.
 *
 * - For 5+ day ranges: returns midnight timestamps (one per day)
 * - For shorter ranges: returns "nice" rounded timestamps (:00, :05, :20, etc.)
 *   at intervals based on range size (1min, 5min, 20min, or hourly)
 *
 * Labels are thinned to fit within the available width, and shifted so
 * midnight aligns when the range crosses midnight.
 */
export const getAxisTimestamps = ({
  minTimestamp,
  maxTimestamp,
  width,
  timeZone,
  perLabelWidth,
}: GetDatesParams): number[] => {
  const labelIntervalMins = getLabelIntervalMins(minTimestamp, maxTimestamp);
  const roundedMin = roundedTimestamp(minTimestamp, labelIntervalMins);
  const roundedMax = roundedTimestamp(maxTimestamp, labelIntervalMins);
  const labelCount = Math.ceil((roundedMax - roundedMin) / 1_000 / 60 / labelIntervalMins);
  const externalLabelPaddingCompensation = 0.5;
  const maxLabelCount = width > 0 ? Math.min(width / perLabelWidth + externalLabelPaddingCompensation, labelCount) : 0;

  const modulo = maxLabelCount > 0 ? Math.ceil(labelCount / maxLabelCount) : 1;

  const timeRangeDays = (maxTimestamp - minTimestamp) / MS_PER_DAY;

  const minDate = new Date(minTimestamp);
  const dayStartMs = getDayStartMs(minDate, timeZone);
  const nextMidnight = getNextMidnight(dayStartMs, timeZone);

  if (timeRangeDays >= 5) {
    const dates: number[] = [];

    let currentMidnight = dayStartMs <= minTimestamp ? nextMidnight : dayStartMs;

    const maxDays = Math.floor(width / perLabelWidth);
    const totalDays = Math.ceil((maxTimestamp - currentMidnight) / MS_PER_DAY);
    const dayModulo = maxDays > 0 ? Math.max(1, Math.ceil(totalDays / maxDays)) : 1;

    let dayIndex = 0;
    while (currentMidnight < maxTimestamp) {
      if (dayIndex % dayModulo === 0) {
        dates.push(currentMidnight);
      }
      currentMidnight = getNextMidnight(currentMidnight, timeZone);
      dayIndex++;
    }

    return dates;
  }

  const timestamps: number[] = [];
  const cadenceMs = labelIntervalMins * MS_PER_MIN;

  for (let i = 0; i <= labelCount; i++) {
    const timestamp = roundedMin + i * cadenceMs;

    if (timestamp > minTimestamp && timestamp < maxTimestamp) {
      timestamps.push(timestamp);
    }
  }

  let currentMidnight = dayStartMs <= minTimestamp ? nextMidnight : dayStartMs;
  const midnights: number[] = [];
  while (currentMidnight < maxTimestamp) {
    midnights.push(currentMidnight);
    currentMidnight = getNextMidnight(currentMidnight, timeZone);
  }

  const firstMidnightIndex = timestamps.indexOf(midnights[0] ?? -1);

  const splitIndex = firstMidnightIndex === -1 ? timestamps.length : firstMidnightIndex;
  const timestampsBeforeFirstMidnight = timestamps.slice(0, splitIndex);
  const timestampsStartingFromFirstMidnight = timestamps.slice(splitIndex);

  const midnightSet = new Set(midnights);
  const midnightIndices = timestampsStartingFromFirstMidnight
    .map((timestamp, idx) => (midnightSet.has(timestamp) ? idx : -1))
    .filter((idx) => idx !== -1);

  let commonDivisor: number = modulo;
  const cadenceThreshold = 1;
  for (let i = modulo; i > cadenceThreshold; i--) {
    if (midnightIndices.every((m) => m % i === 0)) {
      commonDivisor = i;
      break;
    }
  }

  const filteredTimestampsStartingFromFirstMidnight = timestampsStartingFromFirstMidnight.filter(
    (_, idx) => idx % commonDivisor === 0
  );

  const filteredTimestampsBeforeFirstMidnight = timestampsBeforeFirstMidnight.filter((_, idx, arr) => {
    const distanceFromMidnight = arr.length - idx;
    return distanceFromMidnight % commonDivisor === 0;
  });

  return [...filteredTimestampsBeforeFirstMidnight, ...filteredTimestampsStartingFromFirstMidnight];
};
